import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  Chip,
  Stack,
} from '@mui/material';
import { parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

interface AuditLog {
  id: number;
  action: string;
  column_name: string;
  change_summary: string;
  old_value: string;
  new_value: string;
  changed_at: string;
  changed_by: {
    type: string;
    name: string;
    email: string;
  };
  request_context: {
    request_id: string;
    ip_address: string;
    user_agent: string;
  };
  display_name?: string;
  category?: string;
  severity?: 'high' | 'medium' | 'low';
  event_summary?: string;
}

interface TimesheetLogsModalProps {
  open: boolean;
  onClose: () => void;
  timesheetId: number;
  auditLogs: AuditLog[];
  timezone?: string;
}

const TimesheetLogsModal: React.FC<TimesheetLogsModalProps> = ({
  open,
  onClose,
  timesheetId,
  auditLogs,
  timezone = 'America/New_York',
}) => {
  const formatDateTime = (dateString: string) => {
    try {
      return formatInTimeZone(
        parseISO(dateString),
        timezone,
        'MMM dd, yyyy h:mm a zzz',
      );
    } catch (error) {
      return dateString;
    }
  };

  const getActionColor = (action: string) => {
    switch (action.toLowerCase()) {
      case 'create':
        return 'success';
      case 'update':
        return 'primary';
      case 'delete':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatColumnName = (columnName: string) => {
    return columnName
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const formatShortDateTime = (dateString: string) => {
    try {
      return formatInTimeZone(parseISO(dateString), timezone, 'MMM dd, h:mm a');
    } catch {
      return dateString;
    }
  };

  const getCategoryColor = (category?: string): string => {
    switch (category) {
      case 'Time Tracking':
      case 'Break Time':
        return '#1976d2';
      case 'Financial':
        return '#ed6c02';
      case 'Approval':
        return '#2e7d32';
      case 'Quality Control':
        return '#d32f2f';
      case 'Billing':
        return '#7b1fa2';
      default:
        return '#9e9e9e';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '60vh' },
      }}
    >
      <DialogTitle>
        <Typography variant="h6" component="div">
          Timesheet Logs - ID: {timesheetId}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Audit trail of all changes made to this timesheet
        </Typography>
      </DialogTitle>

      <DialogContent>
        {auditLogs.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography variant="body1" color="text.secondary">
              No audit logs found for this timesheet.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={2}>
            {auditLogs.map((log, index) => (
              <Box key={log.id}>
                <Box
                  sx={{
                    py: 1.5,
                    pl: 2,
                    pr: 2,
                    borderLeft: 3,
                    borderColor: getCategoryColor(log.category),
                    borderRadius: 1,
                    backgroundColor: 'background.paper',
                  }}
                >
                  <Typography variant="body2" fontWeight={500}>
                    {log.event_summary ?? log.change_summary}
                  </Typography>

                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mt: 0.5 }}
                  >
                    {log.category ?? 'General'} &middot;{' '}
                    {formatShortDateTime(log.changed_at)}
                  </Typography>

                  {(log.old_value || log.new_value) && (
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      display="block"
                      sx={{ mt: 1, fontFamily: 'monospace' }}
                    >
                      {log.old_value || 'NULL'} &rarr; {log.new_value || 'NULL'}
                    </Typography>
                  )}

                  {!log.event_summary && (
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      display="block"
                      sx={{ mt: 0.5 }}
                    >
                      by {log.changed_by.name}
                    </Typography>
                  )}
                </Box>
                {index < auditLogs.length - 1 && <Divider sx={{ my: 0.5 }} />}
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TimesheetLogsModal;
